import { T, Section, Field } from './UI';
import SignaturePad from './SignaturePad';
import LicenceUpload from './LicenceUpload';

// The staff details form, shared by Add/Edit Staff (managers) and Staff Registration (staff filling
// in their own) so the two are always identical — same fields, same required rules, same licence section.

export const todayISO = () => new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0];
export const dateOnly = (d) => (d ? String(d).split('T')[0] : '');

export const EMPTY_DETAILS = {
  firstNames:'', surname:'', homeAddress:'', dateOfBirth:'', nationality:'', mobilePhone:'',
  emergencyName:'', emergencyPhone:'',
  doctorName:'', doctorPhone:'', doctorAddress:'', allergies:'', medications:'',
  bankName:'', bsb:'', accountName:'', accountNumber:'',
  abn:'',
  hasDriverLicence:true, licenceNumber:'', licenceExpiry:'',
  speaksOtherLanguage:false, languages:'', languageLevel:'', languageMode:'',
  signature:'',
};

const REQUIRED = [
  'firstNames', 'surname', 'homeAddress', 'dateOfBirth', 'mobilePhone',
  'emergencyName', 'emergencyPhone',
  'bankName', 'bsb', 'accountName', 'accountNumber',
  'abn',
];

// Returns { field: true | 'message' } — empty object means valid.
// has = { front: bool, back: bool } — whether each licence photo is on file / chosen.
export const validateDetails = (form, has) => {
  const errs = {};
  REQUIRED.forEach(k => { if (!String(form[k]).trim()) errs[k] = true; });
  if (form.bsb.trim() && form.bsb.replace(/\D/g, '').length !== 6) errs.bsb = 'BSB must be 6 digits';
  if (form.abn.trim() && !/^\d{11}$/.test(form.abn.replace(/\s/g, ''))) errs.abn = 'ABN must be 11 digits';
  if (!form.signature) errs.signature = 'Signature is required — create it and press “Use this signature”';
  if (form.dateOfBirth && form.dateOfBirth > todayISO()) errs.dateOfBirth = 'Date of birth can’t be in the future';
  if (form.hasDriverLicence) {
    if (!form.licenceNumber.trim()) errs.licenceNumber = true;
    if (!form.licenceExpiry) errs.licenceExpiry = true;
    if (!has.front) errs.licenceFront = 'Please upload the front of the licence';
    if (!has.back)  errs.licenceBack  = 'Please upload the back of the licence';
  }
  return errs;
};

const YesNo = ({ value, onChange }) => (
  <div style={{ display:'flex', gap:'8px' }}>
    {[{ val:true, label:'Yes' }, { val:false, label:'No' }].map(o => (
      <button key={String(o.val)} type="button" onClick={()=>onChange(o.val)}
        style={{ flex:1, padding:'11px', borderRadius:'var(--radius)', border:`${value===o.val?'1.5px solid var(--blue)':'1px solid var(--border-md)'}`, background:value===o.val?'var(--blue-light)':'var(--surface)', color:value===o.val?'var(--blue)':'var(--muted)', fontSize:'14px', fontWeight:value===o.val?'500':'400', cursor:'pointer', fontFamily:'inherit' }}>
        {o.label}
      </button>
    ))}
  </div>
);

const Grid = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'0 12px' }}>{children}</div>
);

//   form / errors / set(key, value)   the parent's state
//   licence = { front, back }         each { meta, fetchBlob, onUpload, onRemove } for <LicenceUpload>
//   whose                             'Employee' | 'Your' — wording for the signature label
export default function StaffDetailsFields({ form, errors, set, licence, signatureLabel = 'Employee signature *', licenceNote }) {
  // A helper returning JSX (not a component) so inputs keep focus while typing
  const field = (k, label, { required, type = 'text', placeholder, mono } = {}) => (
    <Field label={required ? `${label} *` : label} error={errors[k] ? (typeof errors[k] === 'string' ? errors[k] : 'Required') : null}>
      <input type={type} value={form[k]} onChange={e=>set(k, e.target.value)} placeholder={placeholder}
        style={{ ...T.input, ...(mono ? { fontFamily:'JetBrains Mono,monospace' } : {}), borderColor: errors[k] ? 'var(--red)' : 'var(--border-md)' }}/>
    </Field>
  );

  return (
    <>
      <Section title="Employee Details" accent>
        <Grid>
          {field('firstNames', 'First name(s)', { required:true })}
          {field('surname', 'Surname', { required:true })}
        </Grid>
        {field('homeAddress', 'Home address', { required:true })}
        <Grid>
          {field('dateOfBirth', 'Date of birth', { required:true, type:'date' })}
          {field('nationality', 'Nationality')}
          {field('mobilePhone', 'Mobile tel number', { required:true, type:'tel' })}
        </Grid>
      </Section>

      <Section title="Emergency Contact Details (required)" accent>
        <Grid>
          {field('emergencyName', 'Name', { required:true })}
          {field('emergencyPhone', 'Contact tel number', { required:true, type:'tel' })}
        </Grid>
      </Section>

      <Section title="Bank Account Details for Payroll" accent>
        <Grid>
          {field('accountName', 'Account name', { required:true })}
          {field('bsb', 'BSB number', { required:true, mono:true, placeholder:'123-456' })}
          {field('accountNumber', 'Account number', { required:true, mono:true })}
          {field('bankName', 'Name of bank', { required:true, placeholder:'e.g. Westpac, CBA, ANZ' })}
        </Grid>
      </Section>

      <Section title="ABN" accent>
        {field('abn', 'ABN', { required:true, mono:true, placeholder:'11 digits' })}
      </Section>

      <Section title="Driver Licence" accent>
        <Field label="Current driver licence?">
          <YesNo value={form.hasDriverLicence} onChange={v=>set('hasDriverLicence', v)}/>
        </Field>
        {form.hasDriverLicence && (
          <>
            <Grid>
              {field('licenceNumber', 'Licence number', { required:true, mono:true })}
              {field('licenceExpiry', 'Expiry date', { required:true, type:'date' })}
            </Grid>
            <LicenceUpload label="Licence — front *" error={errors.licenceFront} {...licence.front}/>
            <LicenceUpload label="Licence — back *"  error={errors.licenceBack}  {...licence.back}/>
            {licenceNote && <p style={{ fontSize:'11px', color:'var(--hint)', lineHeight:1.5, margin:0 }}>{licenceNote}</p>}
          </>
        )}
      </Section>

      <Section title="Medical Information (optional)" accent>
        <Grid>
          {field('doctorName', 'Doctor’s name')}
          {field('doctorPhone', 'Doctor’s contact tel number', { type:'tel' })}
        </Grid>
        {field('doctorAddress', 'Doctor’s address')}
        {field('allergies', 'Allergies')}
        {field('medications', 'Medications')}
      </Section>

      <Section title="Languages" accent>
        <Field label="Languages other than English?">
          <YesNo value={form.speaksOtherLanguage} onChange={v=>set('speaksOtherLanguage', v)}/>
        </Field>
        {form.speaksOtherLanguage && (
          <>
            {field('languages', 'Languages spoken')}
            <Grid>
              <Field label="Knowledge">
                <select value={form.languageLevel} onChange={e=>set('languageLevel', e.target.value)} style={T.input}>
                  <option value="">— Select —</option>
                  <option value="basic">Basic</option>
                  <option value="good">Good</option>
                  <option value="fluent">Fluent</option>
                </select>
              </Field>
              <Field label="Spoken / Written">
                <select value={form.languageMode} onChange={e=>set('languageMode', e.target.value)} style={T.input}>
                  <option value="">— Select —</option>
                  <option value="spoken">Spoken</option>
                  <option value="written">Written</option>
                  <option value="both">Both</option>
                </select>
              </Field>
            </Grid>
          </>
        )}
      </Section>

      <Section title="Signed and Dated" accent>
        <SignaturePad label={signatureLabel} name={`${form.firstNames} ${form.surname}`} value={form.signature} onChange={v=>set('signature', v)}/>
        {errors.signature && <div style={{ fontSize:'11px', color:'var(--red)', marginTop:'4px' }}>{errors.signature}</div>}
        <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'8px' }}>
          Dated: {new Date(todayISO()).toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' })}
        </div>
      </Section>
    </>
  );
}
